import { createContext, useContext, useEffect, useState, useCallback, useRef } from "react";
import { LANGS, translate } from "@/i18n/dict";
import api from "@/lib/api";

const I18nContext = createContext(null);

export function I18nProvider({ children }) {
  const [lang, setLangState] = useState(() => localStorage.getItem("rooster.lang") || "nl");
  const hydrated = useRef(false);

  // On first render, try to pick up the user's saved language from /auth/me
  // (falls back to the localStorage value if the call fails / user is anon).
  // Fase 6.5C — we no longer gate on a client-side token because there IS
  // no client-visible token. Silently swallow 401 for anon visitors.
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/auth/me");
        if (data?.language && data.language !== lang) {
          setLangState(data.language);
        }
      } catch {/* not logged in — ignore */}
      finally { hydrated.current = true; }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    localStorage.setItem("rooster.lang", lang);
    const meta = LANGS.find((l) => l.code === lang);
    document.documentElement.lang = lang;
    document.documentElement.dir = meta?.rtl ? "rtl" : "ltr";
    document.body.classList.toggle("rtl", !!meta?.rtl);
    document.body.classList.toggle("ar", lang === "ar");
    // Persist to backend so the choice sticks across devices & sessions.
    // Fire-and-forget; a silent 401 for anon visitors is expected.
    if (hydrated.current) {
      api.patch("/users/me/language", { language: lang }).catch(() => {});
    }
  }, [lang]);

  const setLang = useCallback((next) => setLangState(next), []);
  const t = useCallback((key) => translate(lang, key), [lang]);
  return (
    <I18nContext.Provider value={{ lang, setLang, t, langs: LANGS }}>
      {children}
    </I18nContext.Provider>
  );
}

export const useI18n = () => useContext(I18nContext);
